import * as React from "react";
import { Cart } from "../../domain";
import { firstTextError, validateText } from "../../domain/influencerService";
import {
  useAvailableAddonsForProduct,
  useAvailableServicesForProduct,
  useCart,
} from "../../hooks";
import { cartProductAddons } from "./addons";
import { editableSections } from "./editability";

const initialDraft = (cartProduct) => ({
  personalizationTo: cartProduct.personalizationTo || "",
  notes: cartProduct.notes || "",
  serviceTexts: {},
  pendingServiceTexts: {},
});

const serviceTextFor = (draft) => (svc) =>
  draft.serviceTexts[svc.cartProductServiceId] ?? svc.customerText ?? "";

const changedServicesFor = (services, textFor) =>
  services.filter(
    (svc) => svc.requiresTextInput && textFor(svc) !== (svc.customerText ?? ""),
  );

const personalizationFrom = (draft) => ({
  notes: draft.notes.trim(),
  personalizationTo: draft.personalizationTo.trim(),
});

const personalizationChanged = (personalization, cartProduct) =>
  personalization.notes !== (cartProduct.notes || "") ||
  personalization.personalizationTo !== (cartProduct.personalizationTo || "");

const withoutKey = (key, map) =>
  Object.fromEntries(Object.entries(map).filter(([k]) => k !== String(key)));

export default function useCartProductEditor(cartProduct) {
  const { cart, refresh } = useCart();
  const { availableAddons } = useAvailableAddonsForProduct(
    cartProduct.productId,
  );
  const { services: availableServices } = useAvailableServicesForProduct(
    cartProduct.productId,
  );
  const [draft, setDraft] = React.useState(() => initialDraft(cartProduct));
  const [error, setError] = React.useState("");

  const { addedAddons, unaddedAddons } = React.useMemo(
    () => cartProductAddons({ availableAddons, cartProduct, cart }),
    [availableAddons, cartProduct, cart],
  );

  const sections = React.useMemo(
    () =>
      editableSections({
        cartProduct,
        addedAddons,
        unaddedAddons,
        availableServices,
      }),
    [cartProduct, addedAddons, unaddedAddons, availableServices],
  );

  const textFor = React.useMemo(() => serviceTextFor(draft), [draft]);

  const setField = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const setServiceText = (cartProductServiceId, text) =>
    setDraft((prev) => ({
      ...prev,
      serviceTexts: { ...prev.serviceTexts, [cartProductServiceId]: text },
    }));

  const setPendingServiceText = (influencerServiceId, text) =>
    setDraft((prev) => ({
      ...prev,
      pendingServiceTexts: {
        ...prev.pendingServiceTexts,
        [influencerServiceId]: text,
      },
    }));

  const save = async () => {
    const changedServices = changedServicesFor(cartProduct.services, textFor);
    const validationError = firstTextError(changedServices, textFor);

    if (validationError) {
      setError(validationError);
      return false;
    }

    const personalization = personalizationFrom(draft);

    try {
      if (personalizationChanged(personalization, cartProduct)) {
        await Cart.updateProduct({
          cartProductId: cartProduct.cartProductId,
          personalization,
        });
      }

      await Promise.all(
        changedServices.map((svc) =>
          Cart.updateServiceText({
            cartProductId: cartProduct.cartProductId,
            cartProductServiceId: svc.cartProductServiceId,
            customerText: textFor(svc),
          }),
        ),
      );

      setError("");
      refresh();
      return true;
    } catch (err) {
      setError(err.message || "Unable to save your changes. Please try again.");
      return false;
    }
  };

  const addAddon = async (addon) => {
    try {
      await Cart.addProductAddon({
        cartProductId: cartProduct.cartProductId,
        addonName: addon.name,
      });
      await refresh();
    } catch {
      setError(`Unable to add "${addon.title}". Please try again.`);
    }
  };

  const runRemoval = async ({ remove, failureMessage }) => {
    try {
      await remove();
      setError("");
      await refresh();
    } catch (err) {
      setError(err?.message || failureMessage);
    }
  };

  const removeAddon = (addon) =>
    runRemoval({
      failureMessage: `Unable to remove "${addon.title}". Please try again.`,
      remove: () =>
        Cart.removeProductAddon({
          cartProductId: cartProduct.cartProductId,
          addonName: addon.name,
        }),
    });

  const removeService = (service) =>
    runRemoval({
      failureMessage: `Unable to remove "${service.label}". Please try again.`,
      remove: () =>
        Cart.removeService({
          cartProductId: cartProduct.cartProductId,
          cartProductServiceId: service.cartProductServiceId,
        }),
    });

  const addService = async (service) => {
    const customerText =
      draft.pendingServiceTexts[service.influencerServiceId] ?? "";
    const validation = service.requiresTextInput
      ? validateText(customerText, service)
      : { valid: true };

    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    try {
      await Cart.addService({
        cartProductId: cartProduct.cartProductId,
        influencerServiceId: service.influencerServiceId,
        customerText: service.requiresTextInput ? customerText.trim() : "",
      });

      setDraft((prev) => ({
        ...prev,
        pendingServiceTexts: withoutKey(
          service.influencerServiceId,
          prev.pendingServiceTexts,
        ),
      }));
      setError("");
      await refresh();
    } catch (err) {
      setError(
        err.message || `Unable to add "${service.label}". Please try again.`,
      );
    }
  };

  return {
    sections,
    addedAddons,
    unaddedAddons,
    draft,
    textFor,
    setField,
    setServiceText,
    setPendingServiceText,
    addAddon,
    removeAddon,
    addService,
    removeService,
    save,
    error,
  };
}
